import { useState } from 'react'
import { Check, Layers, Tag, Trash2, X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Dropdown, DropdownDivider, DropdownItem } from '@/components/ui/Dropdown'
import { Input } from '@/components/ui/Field'
import { countLabel } from '@/lib/format'
import type { Segment } from '@/types'

/**
 * Sticky bar, appears on one or more selections. Same four actions on every
 * review surface (docs/03-page-specs.md §4). Programs adds a fifth, Set
 * segment, when it passes `onSetSegment` (PC-23).
 *
 * Pinned to the bottom of the window while its card runs past it, so the
 * actions show without scrolling to the end of a long list. That needs the
 * card to clip with `overflow-clip`: `overflow-hidden` makes the card a scroll
 * container, and the bar then sticks to the card instead of the window.
 */
export function BulkActionBar({
  count,
  noun,
  tagPool = [],
  onClear,
  onApprove,
  onReject,
  onAddTag,
  onDelete,
  segments = [],
  onSetSegment,
}: {
  count: number
  /** Singular. "program" → "3 programs selected". */
  noun: string
  tagPool?: string[]
  onClear: () => void
  onApprove: () => void
  onReject: () => void
  onAddTag: (tag: string) => void
  onDelete: () => void
  segments?: Segment[]
  /** Null unassigns. Without it, there is no Set segment control. */
  onSetSegment?: (segmentId: string | null) => void
}) {
  const [draft, setDraft] = useState('')
  if (count === 0) return null

  return (
    <div className="sticky bottom-0 z-20 flex flex-wrap items-center gap-2 border-t border-rule bg-surface px-4 py-2.5 shadow-[0_-2px_8px_rgba(0,0,0,0.05)]">
      <span className="text-[13px] font-head font-semibold">
        {countLabel(count, noun)} selected
      </span>

      <Button size="sm" variant="ghost" onClick={onClear}>
        Clear
      </Button>

      <span className="ml-auto flex flex-wrap items-center gap-1.5">
        <Button size="sm" variant="primary" onClick={onApprove}>
          <Check size={12} aria-hidden />
          Approve
        </Button>
        <Button size="sm" variant="danger" onClick={onReject}>
          <X size={12} aria-hidden />
          Reject
        </Button>

        <Dropdown
          align="right"
          width="w-56"
          trigger={
            <Button size="sm">
              <Tag size={12} aria-hidden />
              Add tag
            </Button>
          }
        >
          {(close) => (
            <>
              <div className="px-2 py-1.5">
                <Input
                  autoFocus
                  value={draft}
                  placeholder="New tag"
                  aria-label="New tag for selection"
                  className="h-7 text-[12px]"
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter' || !draft.trim()) return
                    onAddTag(draft.trim())
                    setDraft('')
                    close()
                  }}
                />
              </div>
              {tagPool.length > 0 && <DropdownDivider />}
              {tagPool.slice(0, 8).map((tag) => (
                <DropdownItem
                  key={tag}
                  onClick={() => {
                    onAddTag(tag)
                    close()
                  }}
                >
                  {tag}
                </DropdownItem>
              ))}
            </>
          )}
        </Dropdown>

        {onSetSegment && (
          <Dropdown
            align="right"
            width="w-56"
            trigger={
              <Button size="sm">
                <Layers size={12} aria-hidden />
                Set segment
              </Button>
            }
          >
            {(close) => (
              <div className="max-h-72 overflow-y-auto">
                {segments.map((s) => (
                  <DropdownItem
                    key={s.id}
                    onClick={() => {
                      onSetSegment(s.id)
                      close()
                    }}
                  >
                    {s.name}
                  </DropdownItem>
                ))}
                {segments.length > 0 && <DropdownDivider />}
                <DropdownItem
                  onClick={() => {
                    onSetSegment(null)
                    close()
                  }}
                >
                  Unassigned
                </DropdownItem>
              </div>
            )}
          </Dropdown>
        )}

        <Button size="sm" variant="ghost" className="text-brand-red-700" onClick={onDelete}>
          <Trash2 size={12} aria-hidden />
          Delete
        </Button>
      </span>
    </div>
  )
}
